import React, { useCallback, useEffect, useState } from "react";
import config from "config";
import { getCurrentLanguage, setLanguage, useTranslation } from "locales";
import { getCacheStats, clearAllCache, CacheStats } from "lib/offlineCache";
import { getMediaCacheStats, clearMediaCache, MediaCacheStats } from "lib/mediaCache";
import useTheme from "hooks/useTheme";
import BaseButton from "components/BaseButton";
import BaseForm from "components/BaseForm";
import TagInput from "components/TagInput";
import UploadMedia from "components/UploadMedia";
import VoiceEnrollment from "components/VoiceEnrollment";
import useViewController from "./viewController";
import "./style.scss";

function ProfileSettingsPage() {
  const {
    role,
    fields,
    values,
    onChange,
    onSave,
    errors,
    setErrors,
    onInputAvatar,
    src,
    loadingForm,
    userTags,
    allTags,
    onAddingTag,
    onDeleteTag,
  } = useViewController();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();

  // Cache stats
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [mediaCacheStats, setMediaCacheStats] = useState<MediaCacheStats | null>(null);
  const [clearingCache, setClearingCache] = useState(false);

  useEffect(() => {
    getCacheStats().then(setCacheStats).catch((e) => console.error("Cache stats:", e));
    getMediaCacheStats().then(setMediaCacheStats).catch((e) => console.error("Media cache stats:", e));
  }, []);

  const handleClearCache = useCallback(async () => {
    setClearingCache(true);
    try {
      await Promise.all([clearAllCache(), clearMediaCache()]);
      const [stats, mediaStats] = await Promise.all([getCacheStats(), getMediaCacheStats()]);
      setCacheStats(stats);
      setMediaCacheStats(mediaStats);
    } catch (e) {
      console.error("Failed to clear cache:", e);
    } finally {
      setClearingCache(false);
    }
  }, []);

  return (
    <div className="profile-settings">
      <div className="profile-settings__container-avatar">
        <UploadMedia
          photoClassName="profile-settings__avatar"
          aspectRatio={0}
          accept={["image/*"]}
          onInput={onInputAvatar}
          src={src}
        />
      </div>
      <BaseForm
        className="profile-settings__form"
        fields={fields}
        values={values}
        onChange={onChange}
        onSubmitEnter={onSave}
        errors={errors}
        setErrors={setErrors}
      >
        {role === config.userPerformerRole && (
          <TagInput
            className="mt-4"
            name={"tagInfo"}
            value={userTags}
            suggestions={allTags}
            onDelete={onDeleteTag}
            onAddition={onAddingTag}
            label={t("Practice area tags")}
            tooltipText={t("Start typing tags. Hit tab, comma, or return to complete. Hit backspace/delete to remove.")}
          />
        )}

        <BaseButton
          loading={loadingForm}
          className="profile-settings__button"
          onClick={onSave}
          large={true}
          name="submit"
        >
          {t("Save Changes")}
        </BaseButton>
      </BaseForm>

      <div className="profile-settings__section">
        <h3 className="profile-settings__section-title">{t("Language")}</h3>
        <div className="profile-settings__row">
          <span className="profile-settings__row-label">{t("Interface language")}</span>
          <div className="profile-settings__lang-toggle">
            <button
              type="button"
              className={`profile-settings__lang-option${
                getCurrentLanguage() === "ru" ? " __active" : ""
              }`}
              onClick={() => setLanguage("ru")}
            >
              Русский
            </button>
            <button
              type="button"
              className={`profile-settings__lang-option${
                getCurrentLanguage() === "en" ? " __active" : ""
              }`}
              onClick={() => setLanguage("en")}
            >
              English
            </button>
          </div>
        </div>
      </div>

      <div className="profile-settings__section">
        <h3 className="profile-settings__section-title">{t("Appearance")}</h3>
        <div className="profile-settings__row">
          <span className="profile-settings__row-label">{t("Theme")}</span>
          <button
            type="button"
            className="profile-settings__theme-toggle"
            onClick={toggleTheme}
          >
            {theme === "dark" ? t("Dark") : t("Light")}
          </button>
        </div>
      </div>

      <div className="profile-settings__section">
        <VoiceEnrollment />
      </div>

      <div className="profile-settings__section">
        <h3 className="profile-settings__section-title">{t("Storage")}</h3>
        {cacheStats && (
          <div className="profile-settings__cache-info">
            <div className="profile-settings__cache-row">
              <span>{t("Cached chats")}</span>
              <span>{cacheStats.threads}</span>
            </div>
            <div className="profile-settings__cache-row">
              <span>{t("Cached messages")}</span>
              <span>{cacheStats.messages}</span>
            </div>
            <div className="profile-settings__cache-row">
              <span>{t("Cached users")}</span>
              <span>{cacheStats.users}</span>
            </div>
            <div className="profile-settings__cache-row">
              <span>{t("Cached media")}</span>
              <span>{mediaCacheStats?.count ?? 0}</span>
            </div>
            <div className="profile-settings__cache-row __total">
              <span>{t("Estimated size")}</span>
              <span>{(
                (cacheStats.estimatedSizeBytes + (mediaCacheStats?.estimatedSizeBytes ?? 0)) / (1024 * 1024)
              ).toFixed(2)} MB</span>
            </div>
          </div>
        )}
        <BaseButton
          loading={clearingCache}
          className="profile-settings__clear-cache-btn"
          onClick={handleClearCache}
          large={false}
          name="clear-cache"
        >
          {t("Clear offline cache")}
        </BaseButton>
      </div>
    </div>
  );
}

export default ProfileSettingsPage;
